import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, FileText, CircleCheck as CheckCircle2, Settings, Star, Shield, MessageSquare, FileCheck, Clipboard, ArrowRight, X, Coins, Users, TrendingUp, ShoppingCart, PenLine, Zap, ListChecks, Home as HomeIcon, Handshake } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAnalyticsEvents } from '../hooks/useAnalytics';
import { useCustomPageTitle } from '../hooks/usePageTitle';
import FeatureCard from '../components/home/FeatureCard';


const TESTIMONIALS_GRID = [
  {
    name: 'Marie L., Paris',
    project: 'Rénovation appartement',
    quote: "J'avais 3 devis d'artisans avec des écarts énormes. L'estimation SimulReno m'a permis de comprendre lesquels étaient gonflés et de négocier. 2 400€ économisés sur le chantier final.",
    badge: '2 400€ économisés',
  },
  {
    name: 'Thomas B., Lyon',
    project: 'Extension maison',
    quote: "Je ne savais pas par où commencer. J'ai décrit mon projet à SimulReno AI et en 20 minutes j'avais une liste complète et définitive avec les prix. La banque a accepté mon dossier du premier coup.",
    badge: '5 800€ économisés',
  },
  {
    name: 'Sophie M., Marseille',
    project: 'Rénovation complète',
    quote: "Le PDF professionnel a vraiment fait la différence avec ma banque. Et j'ai pu identifier 3 postes surévalués chez mon artisan grâce au détail poste par poste.",
    badge: '3 200€ économisés',
  },
];

// Composant pour le bouton d'estimation sticky sur mobile
const MobileStickyCTA = () => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 sm:hidden z-50 mobile-sticky-banner">
      <Link
        to="/quick-estimate"
        className="btn bg-primary text-white hover:bg-primary/90 flex items-center justify-center w-full relative"
      >
        <Calculator className="h-6 w-6 mr-2" />
        Démarrez une estimation rapide
        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">GRATUIT</span>
      </Link>
      <button 
        className="absolute -top-3 right-3 bg-white rounded-full p-1 shadow-md"
        onClick={() => setIsVisible(false)}
        aria-label="Fermer la bannière"
      >
        <X className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  );
};

// Calculateur d'économies potentielles
const SavingsCalculator = () => {
  const [projectAmount, setProjectAmount] = useState(10000);
  const estimatedSavings = Math.round(projectAmount * 0.13);
  const roi = Math.round(estimatedSavings / 49.9);

  return (
    <div className="bg-gray-50 p-6 rounded-lg mb-8 shadow-sm">
      <p className="font-medium mb-4 text-sm">Calculez votre économie potentielle :</p>
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
        <div className="flex items-center">
          <span className="mr-2">Budget travaux estimé :</span>
          <select 
            value={projectAmount} 
            onChange={(e) => setProjectAmount(Number(e.target.value))}
            className="p-2 rounded border"
            aria-label="Sélectionnez votre budget de travaux"
          >
            <option value="5000">5 000 €</option>
            <option value="10000">10 000 €</option>
            <option value="20000">20 000 €</option>
            <option value="50000">50 000 €</option>
            <option value="100000">100 000 €</option>
          </select>
        </div>
        <div className="bg-green-100 text-green-800 p-3 rounded-lg font-medium flex-1 text-center">
          Économie potentielle : <span className="font-bold text-xl">{estimatedSavings} €</span>
        </div>
      </div>
      <p className="text-sm text-gray-700">
        Soit un retour sur investissement de <span className="font-bold text-green-600">{roi}x</span> votre achat de crédit !
      </p>
    </div>
  );
};
const LIGHT_BG_STYLE = {
  background: 'linear-gradient(160deg, #f8f7fc 0%, #f0eef8 50%, #ede9f5 100%)',
  position: 'relative' as const,
};

const LIGHT_BG_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" style="position:absolute;top:0;left:0;opacity:0.4;pointer-events:none"><path d="M0,60 C200,20 400,100 600,60 C800,20 1000,80 1200,60 L1200,0 L0,0 Z" fill="rgba(61,26,120,0.04)"/><path d="M0,120 C300,80 600,160 900,120 C1050,100 1150,130 1200,120 L1200,60 C1000,80 800,20 600,60 C400,100 200,20 0,60 Z" fill="rgba(61,26,120,0.03)"/></svg>`;

const Home = () => {
  const { user } = useAuth();
  const { trackButtonClick } = useAnalyticsEvents();
  useCustomPageTitle('Accueil - Estimation travaux de rénovation');
  const detailedEstimatePath = user ? '/estimation/nouvelle' : '/register';
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <div className="bg-white text-gray-700">
      {/* Hero Section with Background Image */}
      <div className="relative">
        {/* Fond d'image avec gradient violet */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(30, 17, 72, 0.5), rgba(61, 26, 120, 0.92)), url(/hero-renovation.jpg)',
            minHeight: '520px'
          }}
          role="img"
          aria-label="Image de fond représentant une rénovation"
        ></div>
        
        {/* Overlay flou sur le côté gauche pour améliorer la lisibilité */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(30, 17, 72, 0.5) 0%, transparent 70%)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(3px)',
            clipPath: 'polygon(0 0, 60% 0, 60% 100%, 0 100%)',
            minHeight: '520px'
          }}
        ></div>
        
        {/* Contenu */}
        <div className="relative z-10 flex items-center" style={{minHeight: '520px'}}>
          <div className="max-w-6xl mx-auto px-6 w-full py-12">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
              Estimez vos travaux de rénovation avec précision.<br />Sans artisan, sans attente.
            </h1>
            <p className="text-base text-white/95 mb-2 leading-relaxed max-w-2xl">
              Décrivez votre projet en langage naturel. SimulReno AI analyse, structure et chiffre vos travaux poste par poste. Plus de 400 types de travaux couverts. Export PDF pour votre dossier bancaire.
            </p>
            <p className="text-sm text-white/70 mb-8 tracking-wide">
              Rapide. Indépendant. Aux prix réels du marché 2026.
            </p>
            <div className="flex flex-col items-start gap-3">
              <Link
                to={detailedEstimatePath}
                onClick={() => trackButtonClick('detailed_estimate_hero', 'home_hero')}
                className="btn bg-accent hover:bg-accent-dark text-white inline-flex items-center justify-center px-8 transition-transform duration-200 hover:scale-105"
              >
                Démarrer mon estimation
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
              <Link
                to="/quick-estimate"
                onClick={() => trackButtonClick('quick_estimate_hero_secondary', 'home_hero')}
                className="text-white/70 hover:text-white/95 text-sm underline underline-offset-2 transition-colors"
              >
                ou commencer par une estimation rapide gratuite
              </Link>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Social Proof Banner */}
      <div className="bg-gray-50 py-2 border-t border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-5 md:gap-10">
            <div className="flex items-center">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="ml-1.5 text-sm text-gray-500">4.8/5 sur 293 avis</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 text-blue-500" />
              <span className="ml-1.5 text-sm text-gray-500">300+ utilisateurs satisfaits</span>
            </div>
            <div className="flex items-center">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="ml-1.5 text-sm text-gray-500">13% d'économies en moyenne</span>
            </div>
            <div className="flex items-center">
              <Shield className="h-4 w-4 text-primary" />
              <span className="ml-1.5 text-sm text-gray-500">Satisfait ou remboursé</span>
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="py-20" style={LIGHT_BG_STYLE}>
        <div dangerouslySetInnerHTML={{__html: LIGHT_BG_SVG}} />
        <div className="max-w-6xl mx-auto px-6 relative">
          <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#3D1A78' }}>
            À quel moment SimulReno vous est utile ?
          </h2>
          <p className="text-center mb-12 max-w-2xl mx-auto text-sm" style={{ color: '#6b7280' }}>
            Que vous soyez propriétaire, investisseur ou acheteur, SimulReno s'adapte à votre moment.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: '01',
                title: 'Avant de vous lancer',
                text: "Vous avez un bien à rénover et vous voulez savoir à quoi vous attendre. Estimez l'enveloppe travaux, testez des hypothèses, affinez votre projet. Si vous êtes investisseur, calculez votre rentabilité avant de faire une offre.",
              },
              {
                number: '02',
                title: 'Pour boucler votre dossier bancaire',
                text: "Votre banque demande un chiffrage des travaux tous corps d'état. SimulReno génère un PDF professionnel détaillé, poste par poste, acceptable pour une demande de prêt.",
              },
              {
                number: '03',
                title: 'Pour négocier avec les artisans',
                text: "Une fois les devis reçus, vous comparez poste par poste. Vous savez exactement où les prix sont gonflés et vous négociez en confiance.",
              },
            ].map((bloc) => (
              <div
                key={bloc.title}
                className="p-8 flex flex-col gap-4 relative"
                style={{
                  background: 'white',
                  border: '0.5px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <span className="text-xs font-bold tracking-widest" style={{ color: '#F97316' }}>{bloc.number}</span>
                <h3 className="font-bold text-base" style={{ color: '#3D1A78' }}>{bloc.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{bloc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SimulReno AI Section */}
      <div className="py-20" style={{ background: 'var(--gradient-sidebar)' }}>
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl font-bold text-white mb-4">
              SimulReno AI : décrivez votre projet, on s'occupe du reste
            </h2>
            <p className="text-base text-white/80 leading-relaxed">
              Plus besoin de connaître le nom des travaux. Décrivez votre situation en quelques phrases, SimulReno AI structure votre projet et présélectionne les postes de travaux à votre place.
            </p>
          </div>

          {/* Exemple chips */}
          <div className="flex flex-wrap justify-center gap-3 mb-14">
            {[
              "J'aimerais créer une chambre dans les combles",
              "J'aimerais transformer un garage en logement",
              "J'aimerais refaire ma salle de bain",
              "J'aimerais abattre un mur pour agrandir mon salon et remplacer la cuisine",
              "J'aimerais améliorer l'isolation de mon logement",
            ].map((example) => (
              <span
                key={example}
                className="px-4 py-2 rounded-full text-sm italic text-white/90"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                "{example}"
              </span>
            ))}
          </div>

          {/* 3 étapes */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Étape 1 */}
            <div className="p-8 flex flex-col items-center text-center" style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 'var(--radius-lg)', border: '0.5px solid rgba(255,255,255,0.15)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <PenLine className="h-6 w-6 text-white" />
              </div>
              <div className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Étape 1</div>
              <h3 className="text-lg font-bold text-white mb-2">Décrivez</h3>
              <p className="text-sm text-white/75 leading-relaxed">
                Expliquez votre projet comme vous le feriez à un ami. Surface, état du bien, ce que vous voulez changer.
              </p>
            </div>

            {/* Étape 2 */}
            <div className="p-8 flex flex-col items-center text-center" style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 'var(--radius-lg)', border: '0.5px solid rgba(255,255,255,0.15)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(249,115,22,0.25)' }}>
                <Zap className="h-6 w-6 text-accent" />
              </div>
              <div className="text-xs font-semibold text-accent/80 uppercase tracking-widest mb-1">Étape 2</div>
              <h3 className="text-lg font-bold text-white mb-2">SimulReno AI affine</h3>
              <p className="text-sm text-white/75 leading-relaxed">
                L'IA vous pose quelques questions ciblées pour préciser les contraintes techniques de votre chantier.
              </p>
            </div>

            {/* Étape 3 */}
            <div className="p-8 flex flex-col items-center text-center" style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 'var(--radius-lg)', border: '0.5px solid rgba(255,255,255,0.15)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <ListChecks className="h-6 w-6 text-white" />
              </div>
              <div className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Étape 3</div>
              <h3 className="text-lg font-bold text-white mb-2">Vous n'avez plus qu'à affiner</h3>
              <p className="text-sm text-white/75 leading-relaxed">
                Les postes de travaux sont présélectionnés et chiffrés. Vous ne partez pas de zéro : ajoutez, retirez, saisissez les quantités.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to={detailedEstimatePath}
              className="btn bg-accent hover:bg-accent-dark text-white inline-flex items-center px-8 transition-transform duration-200 hover:scale-105"
            >
              Essayer SimulReno AI
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <p className="text-sm text-white/50 mt-3">Disponible avec Crédit Unitaire et Abonnement</p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-20" style={LIGHT_BG_STYLE}>
        <div dangerouslySetInnerHTML={{__html: LIGHT_BG_SVG}} />
        <div className="max-w-4xl mx-auto px-6 relative">
          <h2 className="text-2xl font-bold text-primary mb-12 text-center">
            Choisissez votre formule
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* Colonne 1 — Crédit Unitaire */}
            <div className="bg-white p-8 flex flex-col relative pt-10" style={{ border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-sm font-bold px-5 py-1 rounded-full whitespace-nowrap">
                Crédit Unitaire
              </div>
              <div className="mb-6">
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl font-bold text-primary">19,90€</span>
                  <span className="text-gray-500 text-sm">/adresse</span>
                </div>
                <h3 className="text-xl font-bold text-primary mb-1">Crédit Unitaire</h3>
                <p className="text-sm text-gray-500">Pour décider en connaissance de cause</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1 text-sm">
                {[
                  '1 adresse à vie',
                  'Estimations illimitées sur cette adresse',
                  'Plus de 400 types de travaux configurables',
                  'SimulReno AI inclus',
                  'Export PDF professionnel',
                  'Idéal pour dossier bancaire',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/credits/purchase"
                className="btn bg-primary hover:bg-primary/90 text-white w-full text-center transition-transform duration-200 hover:scale-105"
              >
                Acheter un crédit (19,90€)
              </Link>
            </div>

            {/* Colonne 2 — Abonnement */}
            <div className="bg-white p-8 flex flex-col relative pt-10" style={{ border: '2px solid rgb(var(--color-accent))', borderRadius: 'var(--radius-lg)', boxShadow: '0 4px 24px rgba(249,115,22,0.12)' }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-sm font-bold px-5 py-1 rounded-full whitespace-nowrap">
                Abonnement
              </div>
              <div className="mb-6">
                {/* Prix + toggle */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className="text-3xl font-bold text-primary">
                        {isAnnual ? '319,90€' : '29,90€'}
                      </span>
                      <span className="text-gray-500 text-sm">{isAnnual ? '/an' : '/mois'}</span>
                      {isAnnual && (
                        <span className="ml-1 bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">2 mois offerts</span>
                      )}
                    </div>
                    {isAnnual && (
                      <p className="text-xs text-gray-400 mt-0.5">soit 26,66€/mois</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                    <span className={!isAnnual ? 'font-semibold text-primary' : ''}>Mensuel</span>
                    <button
                      onClick={() => setIsAnnual(!isAnnual)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${isAnnual ? 'bg-orange-500' : 'bg-gray-200'}`}
                      aria-label="Basculer mensuel/annuel"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${isAnnual ? 'translate-x-5' : ''}`} />
                    </button>
                    <span className={isAnnual ? 'font-semibold text-primary' : ''}>Annuel</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-primary mb-1">Abonnement Illimité</h3>
                <p className="text-sm text-gray-500">Pour les professionnels et investisseurs</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1 text-sm">
                {[
                  { text: 'Adresses illimitées', bold: true },
                  { text: 'Estimations illimitées sur chaque adresse', bold: false },
                  { text: 'Plus de 400 types de travaux configurables', bold: false },
                  { text: 'SimulReno AI inclus', bold: false },
                  { text: 'Export PDF professionnel', bold: false },
                  { text: 'Idéal pour dossier bancaire', bold: false },
                  { text: 'Support prioritaire', bold: false },
                  { text: 'Résiliation à tout moment', bold: false },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <span className={item.bold ? 'font-semibold' : ''}>{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/credits/purchase"
                className="btn bg-orange-500 hover:bg-orange-600 text-white w-full text-center transition-transform duration-200 hover:scale-105"
              >
                Commencer l'abonnement
              </Link>
            </div>
          </div>

        </div>
      </div>
      {/* Comparative Section */}
      <div className="py-20" style={{ background: 'linear-gradient(135deg, #1E1148 0%, #3D1A78 100%)', position: 'relative' }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white mb-12 text-center">
            Pourquoi choisir <span style={{ color: '#F97316' }}>SimulReno</span> plutôt que demander un devis à un artisan ?
          </h2>

          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="font-semibold text-white" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <tr>
                  <th className="text-left px-6 py-4">Critère</th>
                  <th className="text-left px-6 py-4">Devis artisan</th>
                  <th className="text-left px-6 py-4">SimulReno</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'transparent' }}>
                  <td className="px-6 py-4 font-medium text-white">Temps d'attente</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Plusieurs jours à plusieurs semaines</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>1 à 30 minutes</td>
                </tr>
                <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <td className="px-6 py-4 font-medium text-white">Tarif pour obtenir le devis</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Souvent 50 € à 150 €</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>0 € (rapide) ou 19,90 € (détaillé)</td>
                </tr>
                <tr style={{ background: 'transparent' }}>
                  <td className="px-6 py-4 font-medium text-white">Couverture des postes</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Partielle ou orientée "prestataire"</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>+400 types de travaux, coûts cachés inclus</td>
                </tr>
                <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <td className="px-6 py-4 font-medium text-white">Neutralité</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Lié à ses intérêts de vente</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>Totalement indépendant</td>
                </tr>
                <tr style={{ background: 'transparent' }}>
                  <td className="px-6 py-4 font-medium text-white">Valeur ajoutée</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Devis à lire seul</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>SimulReno AI + base de négociation + outil de décision</td>
                </tr>
                <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <td className="px-6 py-4 font-medium text-white">Retour sur investissement</td>
                  <td className="px-6 py-4" style={{ color: '#F97316' }}>Aucun</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#4ade80' }}>13% d'économies en moyenne (soit 1 300€ pour 10 000€ de travaux)</td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>

          <div className="mt-8 p-6" style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)', borderRadius: 'var(--radius-lg)' }}>
            <p className="text-white">
              <span className="font-bold">Et en plus :</span> SimulReno AI prend en compte automatiquement les contraintes techniques (échafaudage, hauteur sous plafond, humidité, mur porteur, etc.), comme le ferait un vrai professionnel. Et vous pose les bonnes questions avant de chiffrer.
            </p>
          </div>

          <div className="text-center mt-12">
            <Link
              to={detailedEstimatePath}
              className="btn bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center px-6 transition-transform duration-300 hover:scale-105"
            >
              Passer à l'estimation détaillée
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <p className="mt-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Recommandé pour éviter les mauvaises surprises
            </p>
          </div>
        </div>
      </div>

      {/* Detailed Estimation Section */}
      <div className="py-20" style={LIGHT_BG_STYLE}>
        <div dangerouslySetInnerHTML={{__html: LIGHT_BG_SVG}} />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6">
                Une estimation ultra-détaillée
              </h2>
              <p className="text-base text-gray-600 mb-8">
                Configurez plus de 400 types de travaux pour obtenir une estimation précise de votre chantier de rénovation. SimulReno AI vous guide à travers chaque étape, des études préalables jusqu'aux finitions.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { id: 'description', text: 'Description du bien' },
                  { id: 'etudes', text: 'Études & diagnostics' },
                  { id: 'demolition', text: 'Démolition' },
                  { id: 'gros-oeuvre', text: 'Gros-œuvre' },
                  { id: 'vrd', text: 'VRD' },
                  { id: 'isolation-etancheite', text: 'Isolation & étanchéité' },
                  { id: 'cloisons', text: 'Cloisons & aménagements' },
                  { id: 'electricite', text: 'Électricité & plomberie' },
                  { id: 'sanitaire', text: 'Sanitaire & hygiène' },
                  { id: 'revetements', text: 'Revêtements' },
                  { id: 'portes-fenetres', text: 'Portes & fenêtres' },
                  { id: 'finitions', text: 'Finitions' },
                  { id: 'exterieurs', text: 'Extérieurs' },
                  { id: 'resultats', text: 'Résultats' }
                ].map((step) => (
                  <span
                    key={step.id}
                    className="text-xs font-medium px-3 py-1.5"
                    style={{
                      background: '#EEEAF6',
                      border: '0.5px solid rgba(109,40,217,0.12)',
                      borderRadius: '9999px',
                      color: '#3D1A78',
                    }}
                  >
                    {step.text}
                  </span>
                ))}
              </div>
              <Link
                to={detailedEstimatePath}
                className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center transition-transform duration-300 hover:scale-105"
              >
                <Coins className="h-5 w-5 mr-2" />
                Démarrer mon estimation détaillée
                <ArrowRight className="h-6 w-6 ml-2" />
              </Link>
              <p className="text-sm text-gray-500 mt-2">
                19,90€/adresse
              </p>
            </div>
            <div className="relative" style={{ borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden', transform: 'translateZ(0)' }}>
              <img
                src="/3_simulreno-suggested-works.png"
                alt="Travaux suggérés par SimulReno AI"
                className="w-full block"
                style={{ imageRendering: 'auto', WebkitFontSmoothing: 'antialiased', transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
              />
              <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                400+ options de travaux
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Export Section */}
      <div className="py-20" style={{ background: 'linear-gradient(135deg, #1E1148 0%, #3D1A78 100%)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1" style={{ borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.15)', overflow: 'hidden', transform: 'translateZ(0)' }}>
              <img
                src="/6_simulreno-pdf-export.png"
                alt="Export PDF de l'estimation budgétaire SimulReno"
                className="w-full block"
                style={{ imageRendering: 'auto', WebkitFontSmoothing: 'antialiased', transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl font-bold text-white mb-6">
                Une estimation professionnelle pour votre dossier
              </h2>
              <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Exportez votre estimation au format PDF pour :
              </p>
              <div className="space-y-4 mb-8 text-sm">
                <div className="flex items-start space-x-2">
                  <FileCheck className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'white' }} />
                  <div>
                    <h4 className="font-medium text-white">Dossier bancaire</h4>
                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>Un document détaillé et professionnel pour votre demande de prêt</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Clipboard className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'white' }} />
                  <div>
                    <h4 className="font-medium text-white">Négociation avec les artisans</h4>
                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>Comparez les devis et négociez en toute confiance</p>
                  </div>
                </div>
              </div>
              <Link
                to={detailedEstimatePath}
                className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center transition-transform duration-300 hover:scale-105"
              >
                <Coins className="h-5 w-5 mr-2" />
                Créer mon estimation détaillée
                <ArrowRight className="h-6 w-6 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-20" style={LIGHT_BG_STYLE}>
        <div dangerouslySetInnerHTML={{__html: LIGHT_BG_SVG}} />
        <div className="max-w-6xl mx-auto px-6 relative">
          <h2 className="text-2xl font-bold text-primary mb-4 text-center">
            Ils nous font confiance
          </h2>
          <p className="text-center text-base text-gray-600 mb-12 max-w-3xl mx-auto">
            Découvrez comment nos utilisateurs ont économisé sur leurs travaux grâce à nos estimations détaillées.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS_GRID.map((t) => (
              <div
                key={t.name}
                className="p-7 flex flex-col relative overflow-hidden"
                style={{
                  background: 'white',
                  border: '0.5px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                {/* Guillemets décoratifs positionnés en absolu */}
                <div
                  className="absolute top-4 left-5 font-serif select-none pointer-events-none"
                  style={{ fontSize: '80px', color: 'rgba(61,26,120,0.10)', lineHeight: 1 }}
                  aria-hidden="true"
                >"</div>
                <blockquote className="text-sm text-gray-700 leading-relaxed italic flex-1 mb-6 mt-10 relative z-10">
                  {t.quote}
                </blockquote>
                <div className="mb-4">
                  <p className="font-bold text-primary text-sm">{t.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t.project}</p>
                </div>
                <span
                  className="inline-block self-start text-white text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: '#3D1A78' }}
                >
                  {t.badge}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* FAQ Section */}
      <div className="py-20" style={{ background: '#1E1148' }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: '#F97316' }}>
            Questions fréquentes
          </h2>
          <p className="text-center mb-12 max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Nous avons regroupé les questions les plus fréquentes pour vous aider à comprendre notre service
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            {/* Colonne gauche */}
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center text-white">
                <FileText className="h-5 w-5 mr-2" style={{ color: '#F97316' }} />
                À propos des estimations
              </h3>
              <div className="space-y-3">
                {[
                  {
                    q: 'Comment sont calculés les prix ?',
                    a: "Nos prix sont basés sur une base de données de plus de 400 types de travaux, construite à partir de devis réels collectés et analysés par notre équipe. Chaque estimation intègre le coût des matériaux et de la main d'oeuvre, ajusté selon votre localisation.",
                  },
                  {
                    q: 'Et si mon projet est atypique ?',
                    a: "SimulReno AI est justement conçu pour ça. Décrivez votre projet en langage naturel, l'IA identifie les contraintes spécifiques et sélectionne les postes de travaux adaptés à votre situation.",
                  },
                  {
                    q: 'Les prix sont-ils à jour ?',
                    a: "Oui. Notre base de prix est mise à jour régulièrement pour refléter les prix réels du marché. Les estimations affichent le millésime de référence utilisé (actuellement 2026).",
                  },
                  {
                    q: "Puis-je utiliser l'estimation pour mon prêt ?",
                    a: "Oui. L'export PDF professionnel est conçu pour être présenté à votre banque dans le cadre d'une demande de prêt travaux. Il détaille les postes de travaux, les quantités et les montants, tous corps d'état.",
                  },
                ].map(({ q, a }) => (
                  <details
                    key={q}
                    className="p-5 transition-all duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 'var(--radius-md)',
                      border: '0.5px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    <summary className="font-semibold cursor-pointer text-white">{q}</summary>
                    <p className="mt-3 text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>{a}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Colonne droite */}
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center text-white">
                <Coins className="h-5 w-5 mr-2" style={{ color: '#F97316' }} />
                À propos des crédits
              </h3>
              <div className="space-y-3">
                {[
                  {
                    q: 'Comment fonctionnent les crédits SimulReno ?',
                    a: "1 crédit = 1 adresse à vie. Une fois un crédit utilisé sur une adresse, vous pouvez faire autant d'estimations détaillées que vous le souhaitez pour cette adresse, sans limite.",
                  },
                  {
                    q: 'Combien de temps est valable un crédit ?',
                    a: "Un crédit acheté est valable à vie. Il n'expire pas et peut être utilisé quand vous le souhaitez.",
                  },
                  {
                    q: 'SimulReno AI, comment ça marche ?',
                    a: "Décrivez votre projet en langage naturel. SimulReno AI vous pose quelques questions ciblées, puis présélectionne les postes de travaux adaptés à votre chantier. Vous n'avez plus qu'à affiner les quantités. Disponible avec Crédit Unitaire et Abonnement.",
                  },
                  {
                    q: 'Puis-je utiliser SimulReno pour plusieurs projets en même temps ?',
                    a: "Oui. Avec le Crédit Unitaire, chaque crédit correspond à une adresse. Vous pouvez en acheter autant que vous avez de projets. Avec l'Abonnement, toutes vos adresses sont incluses sans limite.",
                  },
                ].map(({ q, a }) => (
                  <details
                    key={q}
                    className="p-5 transition-all duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 'var(--radius-md)',
                      border: '0.5px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    <summary className="font-semibold cursor-pointer text-white">{q}</summary>
                    <p className="mt-3 text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>{a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to={detailedEstimatePath}
              className="btn bg-accent hover:bg-accent-dark text-white inline-flex items-center px-8 transition-transform duration-300 hover:scale-105"
            >
              Obtenir une estimation détaillée
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <MobileStickyCTA />
    </div>
  );
};

export default Home;